import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import { useAuth } from '@/context/AuthContext';

// Blocking click-wrap for the Terms of Service + DPA. No close button and Escape does nothing:
// centre users can't use the app until they accept the current version (or sign out).
export default function TermsGate() {
  const { logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [legal, setLegal] = useState(null);
  const [agree, setAgree] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/legal/status`, { credentials: 'include' });
        if (!res.ok) return;
        const s = await res.json();
        if (cancelled || !s.needs_acceptance) return;
        const t = await fetch(`${API_URL}/api/legal/terms`, { credentials: 'include' });
        if (t.ok && !cancelled) { setLegal(await t.json()); setStatus(s); }
      } catch { /* network blip: the gate shows on the next page load */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status || !legal) return null;

  const isAdmin = status.role === 'admin';

  const accept = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/legal/accept`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: legal.version, agree, typed_name: typedName }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) { toast.error(data.detail); window.location.reload(); return; }
      if (!res.ok) { toast.error(data.detail || 'Could not save, please try again'); return; }
      toast.success('Thanks — terms accepted');
      setStatus(null);
    } finally {
      setSaving(false);
    }
  };

  const Section = ({ s }) => (
    <div className="mb-3">
      <p className="font-semibold text-slate-800 text-sm">{s.heading}</p>
      <p className="text-slate-600 text-sm leading-relaxed">{s.body}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/60 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="terms-title">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-slate-100 flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-pink-500 shrink-0 mt-0.5" />
          <div>
            <h2 id="terms-title" className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isAdmin ? 'Terms of Service & Data Processing Agreement' : 'Terms of Service'}
            </h2>
            <p className="text-xs text-slate-500">
              Version {legal.version} · {legal.company.name}, company {legal.company.number}. Please read and accept to continue.
            </p>
          </div>
        </div>

        <div className="overflow-y-auto px-5 py-4 flex-1">
          {legal.terms.map(s => <Section key={s.heading} s={s} />)}
          {isAdmin && (
            <>
              <p className="font-bold text-slate-900 mt-5 mb-2">Data Processing Agreement</p>
              {legal.dpa.map(s => <Section key={s.heading} s={s} />)}
            </>
          )}
          {!isAdmin && (
            <p className="text-xs text-slate-500 mt-4">Your centre's account owner accepts the Data Processing Agreement on behalf of the business.</p>
          )}
        </div>

        <div className="p-5 border-t border-slate-100 space-y-3">
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="mt-1 accent-pink-600" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            <span>{status.statement}</span>
          </label>
          <Input value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Type your full name to sign" className="rounded-xl h-10" autoComplete="name" />
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" className="rounded-xl text-slate-500" onClick={() => logout && logout()}>Sign out</Button>
            <Button className="btn-primary rounded-xl" onClick={accept} disabled={!agree || typedName.trim().length < 2 || saving}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} I agree
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
